import { Link } from "react-router-dom";
import { PublicNavbar } from "@/components/layout/PublicNavbar";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { useAuth } from "@/hooks/useAuth";
import {
  ArrowRight,
  ShieldCheck,
  Database,
  FileSpreadsheet,
  Activity,
  BookOpen,
} from "lucide-react";

export function HomePage() {
  const { user } = useAuth();

  return (
    <div className="min-h-dvh flex flex-col bg-[#f6f5f4] text-[#000000] selection:bg-[#62aef0]/30 selection:text-[#000000]">
      <PublicNavbar />

      {/* Signature EKSUTH Light Teal Green Hero Band */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#e8f7f2] via-[#eef9f5] to-[#f6f5f4] border-b border-[#d8ece4] py-20 sm:py-28">
        {/* Subtle decorative sticker & ambient mint/teal glow */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-60">
          <div className="absolute -top-12 right-12 h-80 w-80 rounded-full bg-[#14b8a6]/15 blur-3xl" />
          <div className="absolute -bottom-8 left-12 h-72 w-72 rounded-full bg-[#2a9d99]/15 blur-3xl" />
          {/* Subtle playful sticker dots */}
          <div className="absolute top-12 left-1/4 h-2 w-2 rounded-full bg-[#ff64c8]" />
          <div className="absolute top-24 right-1/3 h-2 w-2 rounded-full bg-[#62aef0]" />
          <div className="absolute bottom-16 right-1/4 h-2 w-2 rounded-full bg-[#1aae39]" />
          <div className="absolute bottom-20 left-1/3 h-2 w-2 rounded-full bg-[#dd5b00]" />
        </div>

        <div className="relative mx-auto max-w-5xl px-4 text-center sm:px-6 space-y-6">
          {/* Eyebrow Pill */}
          <div className="inline-flex items-center gap-2 rounded-full bg-white/90 border border-[#bce4d8] px-3.5 py-1 text-xs font-semibold tracking-wide text-[#0d6e5e] shadow-xs">
            <span className="h-2 w-2 rounded-full bg-[#0d9488]" />
            <span>Ekiti State University Teaching Hospital &middot; AMR Surveillance</span>
          </div>

          {/* Display Headline */}
          <h1 className="text-4xl sm:text-6xl font-bold tracking-[-2.125px] text-[#082823] leading-[1.05] max-w-3xl mx-auto">
            Antimicrobial stewardship, made simple.
          </h1>

          {/* Concise Subhead */}
          <p className="text-base sm:text-lg text-[#26534c] max-w-2xl mx-auto font-normal leading-relaxed">
            A quiet, disciplined platform for bench daybook transcription, automated QA rules,
            and CLSI M39 cumulative antibiograms.
          </p>

          {/* Pill CTA Pair */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-3">
            {user ? (
              <Link
                to="/workbench"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0075de] hover:bg-[#005bab] active:scale-[0.98] text-white px-7 py-3 text-[15px] font-medium transition-all shadow-sm"
              >
                <Activity className="h-4 w-4" />
                <span>Open Volunteer Workbench</span>
              </Link>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0075de] hover:bg-[#005bab] active:scale-[0.98] text-white px-7 py-3 text-[15px] font-medium transition-all shadow-sm"
              >
                <span>Access Workbench</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}

            <Link
              to="/docs"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#f2faf7] active:scale-[0.98] text-[#082823] px-7 py-3 text-[15px] font-medium transition-all border border-[#bce4d8] shadow-soft"
            >
              <BookOpen className="h-4 w-4 text-[#0d6e5e]" />
              <span>Read Documentation</span>
            </Link>
          </div>

          {/* Micro Evidence Badge */}
          <div className="pt-6 flex flex-wrap justify-center items-center gap-4 text-xs font-medium text-[#38645c]">
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#1aae39]" />
              CLSI M39 First-Isolate Rules
            </span>
            <span className="hidden sm:inline text-[#a0c5bd]">&middot;</span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#0d9488]" />
              Offline IndexedDB Sync
            </span>
            <span className="hidden sm:inline text-[#a0c5bd]">&middot;</span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#2a9d99]" />
              13 Built-in QA Rules
            </span>
          </div>
        </div>
      </section>

      {/* Main Daylight Document Content (Warm Paper Canvas) */}
      <main className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 space-y-20 flex-1">
        
        {/* Core Capabilities: 3 Clean Feature Cards */}
        <section className="space-y-8">
          <div className="text-center max-w-xl mx-auto space-y-2">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#615d59]">
              <span className="h-2 w-2 rounded-full bg-[#2a9d99]" />
              Core Capabilities
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-[-1px] text-[#000000]">
              Built for bench speed and clinical precision.
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {/* Card 1: Fast Entry */}
            <div className="rounded-xl border border-[#e6e6e6] bg-white p-6 shadow-soft space-y-3">
              <div className="h-9 w-9 rounded-lg bg-[#2a9d99]/10 text-[#2a9d99] flex items-center justify-center font-semibold text-sm">
                <FileSpreadsheet className="h-5 w-5 text-[#2a9d99]" />
              </div>
              <h3 className="text-lg font-bold tracking-[-0.25px] text-[#000000]">
                Dual-Keystroke Entry
              </h3>
              <p className="text-[14px] text-[#615d59] leading-relaxed">
                Optimized keyboard shortcuts for rapid daybook digitization. Works completely
                offline with automatic indexed caching and seamless conflict-free sync.
              </p>
            </div>

            {/* Card 2: QA Rules */}
            <div className="rounded-xl border border-[#e6e6e6] bg-white p-6 shadow-soft space-y-3">
              <div className="h-9 w-9 rounded-lg bg-[#dd5b00]/10 text-[#dd5b00] flex items-center justify-center font-semibold text-sm">
                <ShieldCheck className="h-5 w-5 text-[#dd5b00]" />
              </div>
              <h3 className="text-lg font-bold tracking-[-0.25px] text-[#000000]">
                13 Automated QA Checks
              </h3>
              <p className="text-[14px] text-[#615d59] leading-relaxed">
                Live verification against CLSI intrinsic resistance rules and biological
                plausibility. Ambiguous entries are flagged for microbiologist review.
              </p>
            </div>

            {/* Card 3: M39 Antibiograms */}
            <div className="rounded-xl border border-[#e6e6e6] bg-white p-6 shadow-soft space-y-3">
              <div className="h-9 w-9 rounded-lg bg-[#62aef0]/10 text-[#0075de] flex items-center justify-center font-semibold text-sm">
                <Database className="h-5 w-5 text-[#0075de]" />
              </div>
              <h3 className="text-lg font-bold tracking-[-0.25px] text-[#000000]">
                CLSI M39 Cumulative Tables
              </h3>
              <p className="text-[14px] text-[#615d59] leading-relaxed">
                Instant de-duplication calculating first-isolate %S susceptibility matrices.
                One-click standard BacLink export for national and global WHONET networks.
              </p>
            </div>
          </div>
        </section>

        {/* The 3-Step Stewardship Workflow */}
        <section className="space-y-8 rounded-xl border border-[#e6e6e6] bg-white p-8 sm:p-10 shadow-soft">
          <div className="max-w-xl space-y-2">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#615d59]">
              <span className="h-2 w-2 rounded-full bg-[#ff64c8]" />
              Workflow
            </div>
            <h2 className="text-2xl font-bold tracking-[-0.625px] text-[#000000]">
              From handwritten logbooks to bedside guidance.
            </h2>
          </div>

          <div className="grid gap-6 sm:grid-cols-3 pt-2">
            <div className="space-y-2 border-l-2 border-[#d6b6f6] pl-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#615d59]">
                01 &middot; Transcribe
              </span>
              <h4 className="text-[15px] font-semibold text-[#000000]">Bench Daybook Entry</h4>
              <p className="text-[13px] text-[#615d59] leading-relaxed">
                Trained volunteers transcribe positive cultures and AST measurements directly into standardized fields.
              </p>
            </div>

            <div className="space-y-2 border-l-2 border-[#ff64c8] pl-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#615d59]">
                02 &middot; Verify
              </span>
              <h4 className="text-[15px] font-semibold text-[#000000]">Dual Authorization</h4>
              <p className="text-[13px] text-[#615d59] leading-relaxed">
                Microbiologists inspect flagged anomalies, confirm drug shorthands, and approve isolates for the database.
              </p>
            </div>

            <div className="space-y-2 border-l-2 border-[#1aae39] pl-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#615d59]">
                03 &middot; Guide
              </span>
              <h4 className="text-[15px] font-semibold text-[#000000]">Empirical Prescribing</h4>
              <p className="text-[13px] text-[#615d59] leading-relaxed">
                Clinicians consult hospital-wide and ward-specific antibiograms to select rational empiric antimicrobials.
              </p>
            </div>
          </div>
        </section>

        {/* Reference Standards Strip */}
        <section className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-xl border border-[#e6e6e6] bg-white p-5 text-center shadow-soft">
            <p className="text-2xl font-bold text-[#000000] tracking-tight">18</p>
            <p className="text-xs font-medium text-[#615d59] mt-0.5">Approved Hospital Wards</p>
          </div>
          <div className="rounded-xl border border-[#e6e6e6] bg-white p-5 text-center shadow-soft">
            <p className="text-2xl font-bold text-[#000000] tracking-tight">48</p>
            <p className="text-xs font-medium text-[#615d59] mt-0.5">WHONET Antibiotics</p>
          </div>
          <div className="rounded-xl border border-[#e6e6e6] bg-white p-5 text-center shadow-soft">
            <p className="text-2xl font-bold text-[#000000] tracking-tight">35</p>
            <p className="text-xs font-medium text-[#615d59] mt-0.5">Standardized Organisms</p>
          </div>
          <div className="rounded-xl border border-[#e6e6e6] bg-white p-5 text-center shadow-soft">
            <p className="text-2xl font-bold text-[#000000] tracking-tight">13</p>
            <p className="text-xs font-medium text-[#615d59] mt-0.5">Active QA Rules</p>
          </div>
        </section>

        {/* Minimalist Documentation Banner */}
        <section className="rounded-xl border border-[#e6e6e6] bg-white p-8 sm:p-10 shadow-soft flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="space-y-1.5 max-w-xl">
            <h3 className="text-xl font-bold tracking-tight text-[#000000]">
              Explore Complete Clinical Documentation
            </h3>
            <p className="text-sm text-[#615d59] leading-relaxed">
              Read Appendices A, B, and C reference tables, bench entry protocols, CLSI M39 rules,
              and role-based permissions in the official guide.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link
              to="/docs"
              className="inline-flex items-center gap-2 rounded-full bg-[#0075de] hover:bg-[#005bab] active:scale-[0.98] text-white px-5 py-2.5 text-sm font-medium transition-all shadow-sm"
            >
              <span>Documentation</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

      </main>

      <PublicFooter />
    </div>
  );
}
